import { ReactElement } from "react";
// import { Table, Tbody, Td, Thead, Th, Tr, useTheme, WithCSSVar } from "@chakra-ui/react";
import { Td, Th, useTheme } from "@chakra-ui/react";
import {
  ResultDiagramParam,
  ResultDiagramIndex,
  TableAlign,
} from "../../types";

// 4つの数値からSVGの積み上げ棒グラフを生成する関数（縦棒グラフ）
const createEmotionBarSVG = (
  joy: number,
  anger: number,
  sadness: number,
  calm: number,
  width = 20,
  height = 120,
  theme: any
): ReactElement | null => {
  const total = joy + anger + sadness + calm;
  if (total === 0) return null;

  const colors = {
    joy: theme.colors.primary.emotion_joy,
    anger: theme.colors.primary.emotion_anger,
    sadness: theme.colors.primary.emotion_sadness,
    calm: theme.colors.primary.emotion_calm,
  };

  let currentY = 0; // 上から開始
  const bars: any = [];

  // 各感情の高さを計算してSVG要素を生成（下から上に積み上げ）
  [
    { value: joy, color: colors.joy, name: "喜" },
    { value: anger, color: colors.anger, name: "怒" },
    { value: sadness, color: colors.sadness, name: "哀" },
    { value: calm, color: colors.calm, name: "平" },
  ].forEach((emotion, index) => {
    if (emotion.value > 0) {
      const barHeight = (emotion.value / total) * height;

      bars.push(
        <rect
          key={index}
          x={0}
          y={currentY}
          width={width}
          height={barHeight}
          fill={emotion.color}
          rx={index === 0 ? 2 : 0}
        >
          <title>{`${emotion.name}: ${emotion.value} (${(
            (emotion.value / total) *
            100
          ).toFixed(1)}%)`}</title>
        </rect>
      );
      currentY += barHeight; // 下に向かって積み上げ
    }
  });

  return (
    <svg width={width} height={height} style={{ display: "inline-block" }}>
      {bars}
    </svg>
  );
};

// 感情グラフコンポーネント
const EmotionBar = ({
  joy,
  anger,
  sadness,
  calm,
  width = 20,
  height = 120,
}: {
  joy: number;
  anger: number;
  sadness: number;
  calm: number;
  width?: number;
  height?: number;
}): ReactElement | null => {
  const theme = useTheme();
  return createEmotionBarSVG(joy, anger, sadness, calm, width, height, theme);
};

// 興奮度グラフを生成する関数（縦棒グラフ）
const createExcitementBarSVG = (
  excitement: number,
  maxValue = 10,
  width = 12,
  height = 100,
  theme: any
): ReactElement | null => {
  if (excitement <= 0) return null;
  const barHeight = Math.min((excitement / maxValue) * height, height);
  const color = theme.colors.primary.emotion_excitement;
  return (
    <svg width={width} height={height} style={{ display: "inline-block" }}>
      {/* 背景バー */}
      <rect x={0} y={0} width={width} height={height} fill="#e5e7eb" rx={2} />
      {/* 値バー（下から上に向かって描画） */}
      <rect
        x={0}
        y={height - barHeight}
        width={width}
        height={barHeight}
        fill={color}
        rx={2}
      >
        <title>{`興奮度: ${excitement}/${maxValue} (${(
          (excitement / maxValue) *
          100
        ).toFixed(1)}%)`}</title>
      </rect>
    </svg>
  );
};

// 興奮度グラフコンポーネント
const ExcitementBar = ({
  excitement,
  maxValue = 10,
  width = 12,
  height = 100,
}: {
  excitement: number;
  maxValue?: number;
  width?: number;
  height?: number;
}): ReactElement | null => {
  const theme = useTheme();
  return createExcitementBarSVG(excitement, maxValue, width, height, theme);
};

// 4つの連続する感情データ+興奮度から積み上げグラフを生成するコンポーネント
const EmotionGroupDisplay = ({
  emotionParams,
  showNumbers = true,
  showGraph = true,
  gap = "12px",
}: {
  emotionParams: ResultDiagramParam[];
  showNumbers?: boolean;
  showGraph?: boolean;
  gap?: string;
}): ReactElement | null => {
  const theme = useTheme();
  if (emotionParams.length < 5) return null;

  const joy = Number(emotionParams[0].value) || 0;
  const anger = Number(emotionParams[1].value) || 0;
  const sadness = Number(emotionParams[2].value) || 0;
  const calm = Number(emotionParams[3].value) || 0;
  const excitement = Number(emotionParams[4].value) || 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row", // 横並びに変更
        alignItems: "flex-end", // 下揃えに変更
        gap: gap, // 間隔を広げる
        justifyContent: "center",
      }}
    >
      {/* 4感情グラフ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2px",
        }}
      >
        {showNumbers && (
          <div
            style={{
              fontSize: "11px",
              color: "#666",
              display: "flex",
              flexDirection: "column", // 縦並びに変更
              gap: "2px",
              alignItems: "center",
            }}
          >
            <span style={{ color: theme.colors.primary.emotion_joy }}>
              {joy}
            </span>
            <span style={{ color: theme.colors.primary.emotion_anger }}>
              {anger}
            </span>
            <span style={{ color: theme.colors.primary.emotion_sadness }}>
              {sadness}
            </span>
            <span style={{ color: theme.colors.primary.emotion_calm }}>
              {calm}
            </span>
          </div>
        )}
        {showGraph && (
          <EmotionBar
            joy={joy}
            anger={anger}
            sadness={sadness}
            calm={calm}
            width={10}
            height={54}
          />
        )}
      </div>

      {/* 興奮度グラフ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
        }}
      >
        {showNumbers && (
          <div
            style={{
              fontSize: "11px",
              color: theme.colors.primary.emotion_excitement,
              fontWeight: "500",
            }}
          >
            {excitement}
          </div>
        )}
        {showGraph && (
          <ExcitementBar
            excitement={excitement}
            maxValue={10}
            width={6}
            height={54}
          />
        )}
      </div>
    </div>
  );
};

function CustomTh({
  width,
  subWidth,
  title,
  textAlign,
}: Readonly<{
  key: number;
  width: string;
  subWidth?: string;
  title: string;
  textAlign?: TableAlign;
}>): ReactElement {
  const effectiveAlign = textAlign || "center";
  return (
    <Th
      width={{ base: subWidth ?? width, sm: width }}
      fontSize="sm"
      fontWeight="500"
      borderWidth="1px"
      borderColor="black"
      paddingLeft={effectiveAlign === "left" ? 2 : 0}
      paddingY={1}
      textAlign={effectiveAlign}
      style={
        effectiveAlign === "center"
          ? { paddingInlineStart: 0, paddingInlineEnd: 0 }
          : {}
      }
    >
      {title}
    </Th>
  );
}

function CustomTd({
  value,
  textAlign,
}: Readonly<{
  value: number | string;
  textAlign?: TableAlign;
}>): ReactElement {
  const effectiveAlign = textAlign || "center";
  return (
    <Td
      fontSize="sm"
      fontWeight="300"
      borderWidth="1px"
      borderColor="black"
      paddingY={1}
      textAlign={effectiveAlign}
      paddingX={1}
      paddingInlineStart={{
        base: 1,
        sm: effectiveAlign === "left" ? 15 : 0,
      }}
    >
      {value}
    </Td>
  );
}

export function ResultDiagram(
  props: Readonly<{
    diagramParams: ResultDiagramParam[][];
    diagramIndex: ResultDiagramIndex[];
  }>
): ReactElement {
  const theme = useTheme();
  return (
    <>
      {/* <Table
        style={{ tableLayout: "fixed" }}
        bg="common.base"
        visibility="hidden"
      >
        <Thead bg="#EEEEEE">
          <Tr>
            {props.diagramIndex &&
              props.diagramIndex.map((value, index) => {
                if (index <= 1) {
                  return (
                    <CustomTh
                      key={index}
                      width={value.width}
                      title={value.title}
                      textAlign={value.align}
                    />
                  );
                } else if (index === 2) {
                  return (
                    <CustomTh
                      key={index}
                      width="108px"
                      title="感情バランス"
                      textAlign={value.align}
                    />
                  );
                } else {
                  return null;
                }
              })}
          </Tr>
        </Thead>
        <Tbody>
          {props.diagramParams &&
            props.diagramParams.map((value, index) => (
              <Tr key={index}>
                {value &&
                  value.map((value2, index2) => {
                    if (index2 <= 1) {
                      return (
                        <CustomTd
                          key={index2}
                          value={value2.value}
                          textAlign={value2.align}
                        />
                      );
                    } else if (index2 === 2) {
                      const emotionParams = value.slice(2, 7);
                      return (
                        <td
                          key={index2}
                          style={{
                            textAlign: "center",
                            border: "1px solid black",
                            padding: "8px",
                          }}
                        >
                          <EmotionGroupDisplay
                            showNumbers={false}
                            emotionParams={emotionParams}
                          />
                        </td>
                      );
                    } else {
                      return null;
                    }
                  })}
              </Tr>
            ))}
        </Tbody>
      </Table> */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          marginTop: "16px",
          alignItems: "flex-end", // 下揃え（オプション）
          border: "1px solid #000", // 黒枠を追加
          borderColor: theme.colors.primary.gray_c,
          padding: "12px", // 内側の余白を追加
          overflowX: "auto", // 横スクロールを有効化
          maxWidth: "100%", // 最大幅を制限
          minHeight: "60px", // 最小高さを確保（オプション）
        }}
      >
        {props.diagramParams.map((value, index) => {
          const emotionParams = value.slice(2, 7);
          return (
            <div key={index}>
              <div>
                <EmotionGroupDisplay
                  showNumbers={false}
                  emotionParams={emotionParams}
                  gap="4px"
                />
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  textAlign: "center",
                }}
              >
                {value[0].value}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
